import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import isEqual from 'lodash/isEqual';
import Button from 'yoda-core-components/lib/components/Button';
import {
    AMR_CO_EDIT_SHIPPING,
    AMR_CO_EDIT_PAYMENT,
    AMR_CO_EDIT_BOPIS,
} from 'yoda-checkout-components/lib/common/AmrConstants';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import * as styles from './CheckoutAccordion.css';

const cx = classNames.bind(styles);
/**
 * Accordion Section component renders each section of Accordion component.
 * It is the child component of Accordion Wrapper
 *
 * Helps in making the component loosely coupled so that it can be consumed easily
 */
class CheckoutAccordionSection extends Component {
    /**
     * Supported React properties
     * @type {Object}
     */
    static propTypes = {
        /**
         * Unique id for identifying each accordion section
         * @mandatory prop
         * @type {number}
         */
        index: PropTypes.number.isRequired,

        /**
         * Accordion section title
         * @mandatory prop
         * @type {string}
         */
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,

        /**
         * isAutoCollapsible will autoClose other panes
         * isAutoCollapsible = false will not autoClose other panes
         * @optional prop
         * @type {boolean}
         */
        isAutoCollapsible: PropTypes.bool,

        /**
         * isSelected pane
         * @optional prop
         * @type {boolean}
         */
        isSelected: PropTypes.bool,

        /**
         * onSelect is a  callback on @parent Accordion
         * @prop dependency on isAutoCollapsible prop
         * @type {[type]}
         */
        onSelect: PropTypes.func,

        /**
         * themeConfig provides custom theming capabilities
         * @madatory prop
         * takes default theming or user defined custom theme
         * @type {object}
         */
        themeConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

        /**
         * toExpandAll helps in expanding all AccordionSections
         * @optional/cloned prop
         * takes default value as false or user defined value
         * @type {object}
         */

        toExpandAll: PropTypes.bool,
        /**
         * Children nodes of Accordion wrapper
         * @type {node}
         */

        children: PropTypes.node,
        /**
         * Unique Accordion key used for Accessibility
         * @mandatory prop
         * @type {number}
         */

        accordionKey: PropTypes.number,

        /**
         * To handle edit link
         */
        editCallback: PropTypes.func,
        isEdit: PropTypes.bool,
    };

    /** @properties {Default set up} [optional props set] */
    static defaultProps = {
        isAutoCollapsible: true,
        isSelected: false,
        onSelect: function onSelect(val) {
            return val;
        },
        editCallback: () => {},
        themeConfig: [],
        accordionKey: 1,
        children: '',
        toExpandAll: false,
        isEdit: false,
    };

    constructor(props) {
        super();
        this.editAction = this.editAction.bind(this);

        // isOpen state triggered when isAutoCollapsible prop is set
        this.state = {
            isOpen: props.toExpandAll || props.isSelected,
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {
            themeConfig,
            children,
            title,
            isAutoCollapsible,
            isSelected,
            index,
            accordionKey,
            isEdit,
        } = this.props;
        return (
            !isEqual(this.state, nextState) ||
            !isEqual(children, nextProps.children) ||
            title !== nextProps.title ||
            index !== nextProps.index ||
            isSelected !== nextProps.isSelected ||
            isEdit !== nextProps.isEdit ||
            themeConfig !== nextProps.themeConfig ||
            isAutoCollapsible !== nextProps.isAutoCollapsible ||
            accordionKey !== nextProps.accordionKey
        );
    }

    editAction() {
        if (this.props.isAutoCollapsible) {
            this.props.onSelect(this.props.index);
        }
        this.props.editCallback(this.props.title);
    }

    /**
     * Renders all the children inside it
     * @return {[type]} [description]
     */
    render() {
        const {
            themeConfig,
            children,
            title,
            isAutoCollapsible,
            isSelected,
            index,
            accordionKey,
            isEdit,
        } = this.props;
        const isAccordionSectionExpanded =
            (isAutoCollapsible && isSelected) || (!isAutoCollapsible && this.state.isOpen);

        /**
         * Based on isAutoCollapsible props state is set
         * @type {[type]}
         */
        const containerClass = cx({
            [`${themeConfig.accordionSectionClass}`]: true,
            [`${themeConfig.accordionActiveClass}`]: isAccordionSectionExpanded,
            'review-container': isEdit,
        });

        // const iconState = isAccordionSectionExpanded ? 'icon-minus' : 'icon-plus';
        /**
         * Accessibility Constants to use
         * Basic usage is implemented
         */
        const ariaExpanded =
            (isAutoCollapsible && isSelected) || (!isAutoCollapsible && this.state.isOpen);
        const ariaHidden = !(
            (isAutoCollapsible && isSelected) ||
            (!isAutoCollapsible && this.state.isOpen)
        );

        const accordionHeaderId = `accordionHeader${accordionKey}${index}`;
        const accordionContentId = `accordionContent${accordionKey}${index}`;
        let newReviewHeader = '';
        if (isEdit) {
            newReviewHeader = 'newReviewHeader';
        }
        let amrCodeId = '';
        if (this.props.title === 'Shipping') {
            amrCodeId = AMR_CO_EDIT_SHIPPING;
        } else if (this.props.title === 'Payment') {
            amrCodeId = AMR_CO_EDIT_PAYMENT;
        } else {
            amrCodeId = AMR_CO_EDIT_BOPIS;
        }
        return (
            <div
                className={`${containerClass} ${title === 'Payment' ? dt(['lg:!mb-0']) : ''}`}
                data-automation-id={`accordion-section-${title}`}
            >
                {isEdit ? (
                    <div
                        id={amrCodeId}
                        role="tab"
                        tabIndex="0"
                        className={cx('accordionHeader', 'buttonHeader')}
                        data-automation-id={`accordion-${title}-panel`}
                        aria-expanded={ariaExpanded}
                        aria-controls={accordionContentId}
                        aria-label={title}
                    >
                        <div
                            id={accordionHeaderId}
                            className={cx(
                                'buttonHeader',
                                themeConfig.accordionTitleClass,
                                newReviewHeader
                            )}
                        >
                            <span
                                className={cx('panelNumber')}
                                data-automation-id={`accordion-${title}-panel-no`}
                            >
                                {index}.
                            </span>
                            <span
                                className={cx('panelTitle')}
                                data-automation-id={`accordion-${title}-panel-title`}
                            >
                                {title}
                            </span>
                            <Button
                                onClick={this.editAction}
                                buttonType="Secondary"
                                size="Sm"
                                className={`${cx('editButtonNew')} ${dt([
                                    'float-right',
                                    '!font-normal',
                                    'h-8',
                                    'min-w-4',
                                    'max-w-[50px]',
                                    'm-2',
                                    'p-0',
                                ])}`}
                                automationId={`accordion-${title}-panel-edit-button`}
                            >
                                Edit
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div
                        role="tab"
                        className={styles.nonButtonHeader}
                        data-automation-id={`accordion-${title}-panel`}
                        aria-expanded={ariaExpanded}
                        aria-controls={accordionContentId}
                        aria-label={title}
                    >
                        <div id={accordionHeaderId} className={themeConfig.accordionTitleClass}>
                            <span
                                className={cx('panelNumber')}
                                data-automation-id={`accordion-${title}-panel-no`}
                            >
                                {index}.
                            </span>
                            <span
                                className={cx('panelTitle')}
                                data-automation-id={`accordion-${title}-panel-title`}
                            >
                                {title}
                            </span>
                        </div>
                    </div>
                )}
                <div
                    id={accordionContentId}
                    role="tabpanel"
                    data-automation-id={`accordion-${title}-content-redesign`}
                    className={themeConfig.accordionPanelClass}
                    aria-hidden={ariaHidden}
                    aria-labelledby={accordionHeaderId}
                    tabIndex={ariaHidden ? '-1' : '0'}
                >
                    {children}
                </div>
            </div>
        );
    }
}

export default CheckoutAccordionSection;
